import { definePreset } from '@openng/optimus-ui-themes';
import Lara from '@openng/optimus-ui-themes/lara';

export function toteimTheme(): any {
  return definePreset(Lara, {
    primitive: {
      borderRadius: {
        none: '0',
        xs: '4px',
        sm: '6px',
        md: '8px',
        lg: '10px',
        xl: '12px'
      },
      blue: {
        50: '#B6E2F6',
        100: '#A4DBF4',
        200: '#7FCCF0',
        300: '#5BBEEC',
        400: '#36AFE7',
        500: '#1895CF',
        600: '#1584B7',
        700: '#116992',
        800: '#116992',
        900: '#093549',
        950: '#041A25'
      },
      grey: {
        50: '#FFFFFF',
        100: '#F4F4F4',
        200: '#D0D0D0',
        300: '#ABABAB',
        400: '#868686',
        500: '#616161',
        600: '#5C5C5C',
        700: '#525252',
        800: '#474747',
        900: '#3D3D3D',
        950: '#333333'
      },
      green: {
        50: '#DBE6B3',
        100: '#D4E0A3',
        200: '#CDDB94',
        300: '#C6D685',
        400: '#BFD175',
        500: '#B5CB61',
        600: '#B0C757',
        700: '#A9C247',
        800: '#9FB83D',
        900: '#92A838',
        950: '#859933'
      },
      red: {
        50: '#D87D89',
        100: '#D4707D',
        200: '#D06270',
        300: '#CB5262',
        400: '#C74354',
        500: '#9F303E',
        600: '#842833',
        700: '#78242E',
        800: '#6D212A',
        900: '#631E26',
        950: '#5A1B23'
      },
      background: {
        100: '#F7F7F7',
        900: '#121212'
      },
      midground: {
        100: '#FFFFFF',
        900: '#1E1E1E'
      },
      foreground: {
        100: '#484848',
        500: '#919191',
        900: '#E0E0E0'
      },
      muted: {
        100: '#F4F4F4',
        900: '#121212'
      },
      mutedForeground: {
        100: '#919191',
        900: '#919191'
      },
      info: {
        100: '{indigo.100}',
        900: '{indigo.900}'
      },
      infoForeground: {
        100: '{indigo.900}',
        900: '{indigo.100}'
      },
      success: {
        100: '#EEF3DD',
        900: '#545F2B'
      },
      successForeground: {
        100: '#545F2B',
        900: '#EEF3DD'
      },
      pending: {
        100: '#F4F4F4',
        900: '#919191'
      },
      pendingForeground: {
        100: '#919191',
        900: '#F4F4F4'
      },
      warning: {
        100: '#FFF3D4',
        900: '#75570D'
      },
      warningForeground: {
        100: '#75570D',
        900: '#FFF3D4'
      },
      danger: {
        100: '#FDEDEE',
        900: '#9F303E'
      },
      dangerForeground: {
        100: '#9F303E',
        900: '#FDEDEE'
      }
    },
    semantic: {
      transitionDuration: '0s',
      focusRing: {
        width: '2px',
        style: 'solid',
        color: '{primary.color}',
        offset: '2px',
        shadow: 'none'
      },
      iconSize: '1rem',
      anchorGutter: '0',
      primary: {
        50: '{blue.50}',
        100: '{blue.100}',
        200: '{blue.200}',
        300: '{blue.300}',
        400: '{blue.400}',
        500: '{blue.500}',
        600: '{blue.600}',
        700: '{blue.700}',
        800: '{blue.800}',
        900: '{blue.900}',
        950: '{blue.950}'
      },
      secondary: {
        50: '{grey.50}',
        100: '{grey.100}',
        200: '{grey.200}',
        300: '{grey.300}',
        400: '{grey.400}',
        500: '{grey.500}',
        600: '{grey.600}',
        700: '{grey.700}',
        800: '{grey.800}',
        900: '{grey.900}',
        950: '{grey.950}'
      },
      accent: {
        50: '{green.50}',
        100: '{green.100}',
        200: '{green.200}',
        300: '{green.300}',
        400: '{green.400}',
        500: '{green.500}',
        600: '{green.600}',
        700: '{green.700}',
        800: '{green.800}',
        900: '{green.900}',
        950: '{green.950}'
      },
      destructive: {
        50: '{red.50}',
        100: '{red.100}',
        200: '{red.200}',
        300: '{red.300}',
        400: '{red.400}',
        500: '{red.500}',
        600: '{red.600}',
        700: '{red.700}',
        800: '{red.800}',
        900: '{red.900}',
        950: '{red.950}'
      },
      formField: {
        paddingX: '0.75rem',
        paddingY: '0.5rem',
        borderRadius: '{border.radius.xs}',
        focusRing: {
          width: '2px',
          style: 'solid',
          color: '{primary.color}',
          offset: '-1px',
          shadow: 'none'
        },
        transitionDuration: '{transition.duration}'
      },
      list: {
        padding: '0.125rem 0',
        gap: '0',
        header: {
          padding: '0.5rem 0.75rem 0.375rem 0.75rem'
        },
        option: {
          padding: '0.5rem 0.75rem',
          borderRadius: '0'
        },
        optionGroup: {
          padding: '0.5rem 0.75rem',
          fontWeight: '700'
        }
      },
      content: {
        borderRadius: '{border.radius.xs}'
      },
      mask: {
        transitionDuration: '0.15s'
      },
      navigation: {
        list: {
          padding: '0.125rem 0',
          gap: '0'
        },
        item: {
          padding: '0.5rem 0.75rem',
          borderRadius: '0',
          gap: '0.5rem'
        },
        submenuLabel: {
          padding: '0.5rem 0.75rem',
          fontWeight: '700'
        },
        submenuIcon: {
          size: '0.875rem'
        }
      },
      overlay: {
        select: {
          borderRadius: '{border.radius.xs}',
          shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)'
        },
        popover: {
          borderRadius: '{border.radius.xs}',
          padding: '0.75rem',
          shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)'
        },
        modal: {
          borderRadius: '{border.radius.xs}',
          padding: '1.25rem',
          shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
        },
        navigation: {
          shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)'
        }
      },
      colorScheme: {
        light: {
          surface: {
            0: '#ffffff',
            50: '{slate.50}',
            100: '{slate.100}',
            200: '{slate.200}',
            300: '{slate.300}',
            400: '{slate.400}',
            500: '{slate.500}',
            600: '{slate.600}',
            700: '{slate.700}',
            800: '{slate.800}',
            900: '{slate.900}',
            950: '{slate.950}'
          },
          primary: {
            color: '{primary.500}',
            contrastColor: '#ffffff',
            hoverColor: '{primary.700}',
            activeColor: '{primary.800}'
          },
          highlight: {
            background: '{primary.600}',
            focusBackground: '{primary.700}',
            color: '#ffffff',
            focusColor: '#ffffff'
          },
          mask: {
            background: 'rgba(0,0,0,0.4)',
            color: '{surface.200}'
          },
          formField: {
            background: '{surface.0}',
            disabledBackground: '{surface.300}',
            filledBackground: '{surface.100}',
            filledFocusBackground: '{surface.100}',
            borderColor: '{surface.500}',
            hoverBorderColor: '{surface.500}',
            focusBorderColor: '{surface.500}',
            invalidBorderColor: '{red.500}',
            color: '{surface.900}',
            disabledColor: '{surface.600}',
            placeholderColor: '{surface.600}',
            invalidPlaceholderColor: '{red.500}',
            floatLabelColor: '{surface.600}',
            floatLabelFocusColor: '{surface.600}',
            floatLabelInvalidColor: '{red.500}',
            iconColor: '{surface.900}',
            shadow: 'none'
          },
          text: {
            color: '{foreground.100}',
            hoverColor: '{surface.950}',
            mutedColor: '{surface.600}',
            hoverMutedColor: '{surface.700}'
          },
          content: {
            background: '{surface.0}',
            hoverBackground: '{surface.200}',
            borderColor: '{surface.400}',
            color: '{text.color}',
            hoverColor: '{text.hover.color}'
          },
          overlay: {
            select: {
              background: '{surface.0}',
              borderColor: 'transparent',
              color: '{text.color}'
            },
            popover: {
              background: '{surface.0}',
              borderColor: 'transparent',
              color: '{text.color}'
            },
            modal: {
              background: '{surface.0}',
              borderColor: 'transparent',
              color: '{text.color}'
            }
          },
          list: {
            option: {
              focusBackground: '{surface.200}',
              selectedBackground: '{highlight.background}',
              selectedFocusBackground: '{highlight.focus.background}',
              color: '{text.color}',
              focusColor: '{text.hover.color}',
              selectedColor: '{highlight.color}',
              selectedFocusColor: '{highlight.focus.color}',
              icon: {
                color: '{text.muted.color}',
                focusColor: '{text.hover.muted.color}'
              }
            },
            optionGroup: {
              background: 'transparent',
              color: '{text.color}'
            }
          },
          navigation: {
            item: {
              focusBackground: '{primary.color}',
              activeBackground: '{surface.200}',
              color: '{text.color}',
              focusColor: '{primary.contrast.color}',
              activeColor: '{text.hover.color}',
              icon: {
                color: '{text.muted.color}',
                focusColor: '{primary.contrast.color}',
                activeColor: '{text.hover.muted.color}'
              }
            },
            submenuLabel: {
              background: 'transparent',
              color: '{text.color}'
            },
            submenuIcon: {
              color: '{text.muted.color}',
              focusColor: '{primary.contrast.color}',
              activeColor: '{text.hover.muted.color}'
            }
          }
        },
        dark: {
          surface: {
            0: '#ffffff',
            50: '{zinc.50}',
            100: '{zinc.100}',
            200: '{zinc.200}',
            300: '{zinc.300}',
            400: '{zinc.400}',
            500: '{zinc.500}',
            600: '{zinc.600}',
            700: '{zinc.700}',
            800: '{zinc.800}',
            900: '{zinc.900}',
            950: '#303030'
          },
          primary: {
            color: '{primary.400}',
            contrastColor: '{surface.950}',
            hoverColor: '{primary.300}',
            activeColor: '{primary.200}'
          },
          highlight: {
            background: '{primary.500}',
            focusBackground: '{primary.400}',
            color: '{surface.950}',
            focusColor: '{surface.950}'
          },
          mask: {
            background: 'rgba(0,0,0,0.6)',
            color: '{surface.200}'
          },
          formField: {
            background: '{surface.950}',
            disabledBackground: '{surface.700}',
            filledBackground: '{surface.800}',
            filledFocusBackground: '{surface.800}',
            borderColor: '{surface.500}',
            hoverBorderColor: '{surface.500}',
            focusBorderColor: '{surface.500}',
            invalidBorderColor: '{red.200}',
            color: '{surface.0}',
            disabledColor: '{surface.400}',
            placeholderColor: '{surface.400}',
            invalidPlaceholderColor: '{red.200}',
            floatLabelColor: '{surface.400}',
            floatLabelFocusColor: '{surface.400}',
            floatLabelInvalidColor: '{red.200}',
            iconColor: '{surface.0}',
            shadow: 'none'
          },
          text: {
            color: '{foreground.900}',
            hoverColor: '{surface.0}',
            mutedColor: '{surface.400}',
            hoverMutedColor: '{surface.300}'
          },
          content: {
            background: '{surface.900}',
            hoverBackground: '{surface.700}',
            borderColor: '{surface.500}',
            color: '{text.color}',
            hoverColor: '{text.hover.color}'
          },
          overlay: {
            select: {
              background: '{surface.900}',
              borderColor: '{surface.700}',
              color: '{text.color}'
            },
            popover: {
              background: '{surface.900}',
              borderColor: '{surface.700}',
              color: '{text.color}'
            },
            modal: {
              background: '{surface.900}',
              borderColor: '{surface.700}',
              color: '{text.color}'
            }
          },
          list: {
            option: {
              focusBackground: '{surface.700}',
              selectedBackground: '{highlight.background}',
              selectedFocusBackground: '{highlight.focus.background}',
              color: '{text.color}',
              focusColor: '{text.hover.color}',
              selectedColor: '{highlight.color}',
              selectedFocusColor: '{highlight.focus.color}',
              icon: {
                color: '{text.muted.color}',
                focusColor: '{text.hover.muted.color}'
              }
            },
            optionGroup: {
              background: 'transparent',
              color: '{text.color}'
            }
          },
          navigation: {
            item: {
              focusBackground: '{primary.color}',
              activeBackground: '{surface.700}',
              color: '{text.color}',
              focusColor: '{primary.contrast.color}',
              activeColor: '{text.color}',
              icon: {
                color: '{text.muted.color}',
                focusColor: '{primary.contrast.color}',
                activeColor: '{text.hover.muted.color}'
              }
            },
            submenuLabel: {
              background: 'transparent',
              color: '{text.color}'
            },
            submenuIcon: {
              color: '{text.muted.color}',
              focusColor: '{primary.contrast.color}',
              activeColor: '{text.hover.muted.color}'
            }
          }
        }
      }
    },
    components: {
      inputtext: {
        root: {
          borderRadius: '{border.radius.lg}',
          borderColor: '{foreground.500}',
          focusRing: {
            color: '{foreground.500}'
          }
        }
      },
      inputgroup: {
        addon: {
          borderRadius: '{border.radius.lg}'
        }
      },
      accordion: {
        colorScheme: {
          light: {
            header: {
              background: '{midground.100}',
              first: {
                topBorderRadius: '{border.radius.lg}'
              },
              last: {
                bottomBorderRadius: '{border.radius.lg}'
              },
              borderRadius: '{border.radius.lg}',
              borderColor: '{primary.50}'
            },
            content: {
              background: '{midground.100}',
              borderColor: '{primary.50}'
            }
          },
          dark: {
            header: {
              background: '{midground.200}',
              first: {
                topBorderRadius: '{border.radius.lg}'
              },
              last: {
                bottomBorderRadius: '{border.radius.lg}'
              },
              borderRadius: '{border.radius.lg}',
              borderColor: '{foreground.500}'
            },
            content: {
              background: '{midground.200}',
              borderColor: '{foreground.500}'
            }
          }
        }
      },
      button: {
        root: {
          borderRadius: '{border.radius.lg}'
        },
        colorScheme: {
          light: {
            root: {
              success: {
                color: '{success.100}'
              },
              contrast: {
                background: '{accent.500}',
                borderColor: '{accent.500}',
                hoverBackground: '{accent.800}',
                hoverBorderColor: '{accent.800}',
                activeBackground: '{accent.950}',
                activeBorderColor: '{accent.950}'
              }
            }
          },
          dark: {
            root: {
              success: {
                color: '{success.900}'
              },
              contrast: {
                background: '{accent.500}',
                borderColor: '{accent.500}',
                hoverBackground: '{accent.800}',
                hoverBorderColor: '{accent.800}',
                activeBackground: '{accent.950}',
                activeBorderColor: '{accent.950}'
              }
            }
          }
        }
      },
      card: {
        root: {
          borderRadius: '{border.radius.lg}'
        }
      },
      message: {
        root: {
          borderRadius: '{border.radius.lg}'
        },
        colorScheme: {
          light: {
            success: {
              background: '{success.100}',
              color: '{successForeground.100}'
            },
            error: {
              background: '{danger.100}',
              color: '{dangerForeground.100}'
            }
          },
          dark: {
            success: {
              background: '{success.900}',
              color: '{successForeground.900}'
            },
            error: {
              background: '{danger.900}',
              color: '{dangerForeground.900}'
            }
          }
        }
      },
      progressspinner: {
        colorScheme: {
          light: {
            root: {
              colorOne: '{accent.100}',
              colorTwo: '{accent.400}',
              colorThree: '{accent.700}',
              colorFour: '{accent.900}'
            }
          },
          dark: {
            root: {
              colorOne: '{accent.100}',
              colorTwo: '{accent.400}',
              colorThree: '{accent.700}',
              colorFour: '{accent.900}'
            }
          }
        }
      },
      toast: {
        colorScheme: {
          light: {
            error: {
              background: '{danger.100}',
              color: '{dangerForeground.100}',
              borderColor: '{dangerForeground.100}',
              closeButton: {
                hoverBackground: 'none'
              }
            }
          },
          dark: {
            error: {
              background: '{danger.900}',
              color: '{dangerForeground.900}',
              borderColor: '{dangerForeground.900}',
              closeButton: {
                hoverBackground: 'none'
              }
            }
          }
        }
      },
      autocomplete: {
        dropdown: {
          borderRadius: '{border.radius.lg}'
        }
      },
      treeselect: {
        root: {
          borderRadius: '{border.radius.lg}',
          borderColor: '{foreground.500}',
          hoverBorderColor: '{surface.500}',
          focusRing: {
            color: '{foreground.500}'
          }
        }
      },
      multiselect: {
        root: {
          borderRadius: '{border.radius.lg}',
          focusRing: {
            color: '{foreground.500}'
          }
        }
      },
      select: {
        root: {
          borderRadius: '{border.radius.lg}',
          focusRing: {
            color: '{foreground.500}'
          }
        }
      },
      menu: {
        list: {
          padding: '4px'
        },
        item: {
          borderRadius: '{border.radius.sm}'
        }
      },
      stepper: {
        colorScheme: {
          dark: {
            steppanel: {
              background: '{midground.200}'
            }
          }
        }
      },
      tag: {
        colorScheme: {
          light: {
            success: {
              background: '{success.100}',
              color: '{successForeground.100}'
            },
            warn: {
              background: '{warning.100}',
              color: '{warningForeground.100}'
            },
            info: {
              background: '{blue.500}',
              color: '{foreground.900}'
            },
            danger: {
              background: '{danger.100}',
              color: '{dangerForeground.100}'
            },
            secondary: {
              background: '{secondary.100}',
              color: '{secondaryForeground.100}'
            }
          },
          dark: {
            success: {
              background: '{success.900}',
              color: '{successForeground.900}'
            },
            warn: {
              background: '{warning.900}',
              color: '{warningForeground.900}'
            },
            info: {
              background: '{blue.500}',
              color: '{foreground.900}'
            },
            danger: {
              background: '{danger.900}',
              color: '{dangerForeground.900}'
            },
            secondary: {
              background: '{secondary.900}',
              color: '{secondaryForeground.900}'
            }
          }
        }
      },
      tabs: {
        colorScheme: {
          light: {
            tab: {
              color: '{text.color}',
              background: 'transparent',
              hoverBackground: '{midground.200}',
              activeBackground: '{midground.200}',
              activeColor: '{primary.500}',
              borderWidth: '0'
            }
          },
          dark: {
            tab: {
              color: '{text.color}',
              background: 'transparent',
              hoverBackground: '{midground.200}',
              activeBackground: '{midground.200}',
              activeColor: '{primary.500}',
              borderWidth: '0'
            }
          }
        },
        tab: {
          fontWeight: '500'
        },
        tablist: {
          background: '{midground.200}'
        },
        activeBar: {
          height: '2px',
          background: '{primary.500}'
        }
      }
    }
  });
}
